<script setup>
// Page 1 — Hero: the thesis. Outcome headline + the signature money-flow instrument.
// The headline splits into words and rises in on load (it's the first thing visible,
// so this plays immediately rather than waiting on a scroll trigger), while two civic
// accent squares drift at their own pace as the page scrolls past.
import { ref, onMounted, onUnmounted } from "vue";
import { useI18n } from "vue-i18n";
import { ChevronDown, ArrowRight } from "@lucide/vue";
import HeroFlow from "@/components/landing/HeroFlow.vue";
import { buttonVariants } from "@/components/ui/button";
import { anchorScroll } from "@/lib/scroll";
import { gsap, useMotion, parallax, reducedMotion, EASE, splitHeadlineWords, vMagnetic } from "@/lib/motion";

const { t } = useI18n();

// The first-screen proof row: where the money lands, what authorises it, what it
// costs. Price belongs here rather than "maandelijks opzegbaar" (which moved to the
// pricing page) — a board comparing options wants the number before it scrolls.
const facts = [
  { key: "own", label: "landing.hero.facts.own" },
  // Each of these takes its own line at 390px, and the hero has one screen. The
  // mandate is made at length on the geldstroom page and in two FAQ answers, so it's
  // the one that sits out the phone layout; where the money lands and what it costs
  // are what a board scanning a first screen is weighing.
  { key: "sepa", label: "landing.hero.facts.sepa", phoneHidden: true },
  { key: "price", label: "landing.hero.facts.price" },
];

const root = ref(null);
const heroHeadline = ref(null);
const heroLead = ref(null);
const heroCtas = ref(null);
const heroManagers = ref(null);
const heroFacts = ref(null);
const squareA = ref(null);
const squareB = ref(null);

let cleanup = () => {};

onMounted(() => {
  cleanup = useMotion(root.value, () => {
    // The headline rises word by word, then the rest of the hero column follows in
    // one breath. Reduced-motion visitors just see it — no split, no DOM churn,
    // straight to the settled page.
    if (!reducedMotion) {
      // The masks this leaves behind have room for the tails of the g and j in
      // "bijdragen", so the words land in their finished rendering and the headline is
      // never redrawn afterwards — see splitHeadlineWords.
      const split = splitHeadlineWords(heroHeadline.value);
      gsap.set([heroLead.value, heroCtas.value, heroManagers.value, heroFacts.value], { opacity: 0, y: 16 });

      gsap
        .timeline({ delay: 0.1 })
        .to(split.words, { opacity: 1, y: "0%", duration: 0.7, ease: EASE, stagger: 0.045 })
        .to(heroLead.value, { opacity: 1, y: 0, duration: 0.5, ease: EASE }, "-=0.35")
        .to(heroCtas.value, { opacity: 1, y: 0, duration: 0.45, ease: EASE }, "-=0.25")
        .to(heroManagers.value, { opacity: 1, y: 0, duration: 0.4, ease: EASE }, "-=0.3")
        .to(heroFacts.value, { opacity: 1, y: 0, duration: 0.45, ease: EASE }, "-=0.25");
    }

    // Depth without hijacking scroll: two civic accent squares drift at their own pace.
    parallax(squareA.value, { distance: 160 });
    parallax(squareB.value, { distance: -120 });
  });
});

onUnmounted(() => cleanup());
</script>

<template>
  <section ref="root" class="snap-page page-panel relative flex items-center overflow-hidden" :data-page-label="t('landing.pager.hero')">
    <div class="pointer-events-none absolute inset-0 -z-10" aria-hidden="true">
      <span ref="squareA" class="absolute right-[6%] top-24 hidden size-28 border-2 border-primary/25 lg:block" />
      <span ref="squareB" class="absolute left-[3%] bottom-16 hidden size-20 bg-secondary lg:block" />
    </div>

    <div class="mx-auto grid w-full max-w-6xl items-center gap-3 px-5 py-1.5 sm:gap-10 sm:py-12 lg:grid-cols-[1.05fr_1fr] lg:gap-14 lg:py-20">
      <div class="flex flex-col items-start">
        <h1
          ref="heroHeadline"
          class="max-w-xl font-sans text-[1.625rem] font-bold leading-[1.08] tracking-tight text-foreground sm:text-5xl sm:leading-[1.05] lg:text-6xl"
        >
          {{ t("landing.hero.headline") }}
        </h1>
        <p ref="heroLead" class="mt-2 max-w-xl text-sm leading-snug text-muted-foreground sm:mt-6 sm:text-lg sm:leading-relaxed">
          {{ t("landing.hero.lead") }}
        </p>
        <!-- Full-width stacked buttons on a phone (they never fit side by side at
             this copy length) so the pair reads as a deliberate block rather than
             two wrapped pills.
             The filled button is the LOW-commitment one: it scrolls to the section
             that shows how the month actually runs. A volunteer treasurer or a
             manager comparing tools wants to see the mechanism before creating an
             account, and signing up sits right next to it for whoever is already
             sold. -->
        <div ref="heroCtas" class="mt-4 flex w-full flex-col items-stretch gap-2 sm:mt-8 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
          <span v-magnetic class="flex sm:inline-block">
            <a href="#hoe-het-werkt" :class="[buttonVariants({ size: 'lg' }), 'h-10 w-full sm:h-12 sm:w-auto']" @click="anchorScroll">
              {{ t("landing.hero.cta") }}<ChevronDown class="size-4" />
            </a>
          </span>
          <RouterLink :to="{ path: '/signup', query: { voor: 'vve' } }" :class="[buttonVariants({ variant: 'outline', size: 'lg' }), 'h-10 w-full sm:h-12 sm:w-auto']">
            {{ t("landing.hero.cta_secondary") }}<ArrowRight class="size-4" />
          </RouterLink>
        </div>
        <!-- The manager route, stated on the first screen rather than left to the
             footer: a beheerder with a portfolio is a different sale, and the eyebrow
             already invited them in. -->
        <p ref="heroManagers" class="mt-2.5 text-xs text-muted-foreground sm:mt-5 sm:text-sm">
          {{ t("landing.hero.managers_q") }}
          <RouterLink to="/voor-vve-beheerders" class="font-semibold text-primary underline-offset-4 hover:underline">{{ t("landing.hero.managers_link") }}</RouterLink>
        </p>
        <!-- Plain mono facts, one per line — quiet instrument copy, no list dressing. -->
        <ul ref="heroFacts" class="mt-2 flex flex-wrap gap-x-5 gap-y-0.5 sm:mt-6 sm:gap-y-2">
          <li
            v-for="f in facts"
            :key="f.key"
            class="font-mono text-[0.625rem] font-semibold uppercase tracking-[0.08em] text-muted-foreground sm:text-[0.6875rem]"
            :class="{ 'hidden sm:block': f.phoneHidden }"
          >
            {{ t(f.label) }}
          </li>
        </ul>
      </div>

      <HeroFlow
        :top-label="t('landing.flow.label')"
        :owners-label="t('landing.flow.from_label')"
        :amount-cents="475000"
        :to-label="t('landing.flow.to_label')"
        :iban-label="t('landing.flow.to_iban')"
        :landed-suffix="t('landing.flow.landed_suffix')"
        :pending-suffix="t('landing.flow.pending_suffix')"
        :via-label="t('landing.flow.via_label')"
        :via-value="t('landing.flow.via_value')"
        :untouched-label="t('landing.flow.untouched')"
      />
    </div>
  </section>
</template>
